/**
 * DS-internal hook : le souffle de la pause zen (`<CorrectionPause kind="breathing">`).
 *
 * Inspire, expire, chaque temps à la durée `half` (`breath.half`, `theme/tokens/motion`) : le mot du
 * temps (`breath`) et la valeur animée de la bulle (`progress`, 0 expiré, 1 inspiré), qui grandit et
 * rapetisse en douceur au lieu de sauter d'une taille à l'autre. Le premier temps est une
 * inspiration : la bulle part de l'expiré.
 *
 * Un état d'INTERFACE : le rythme ne dit rien du métier, il vit ici et non dans l'écran.
 *
 * En mouvement réduit, rien ne tourne : pas de minuteur, la bulle posée à sa taille d'inspiration,
 * `still` vrai (la pause dit alors ses deux temps d'un coup, sans les alterner).
 */
import { useEffect, useRef, useState } from "react"
import { Animated, Easing, Platform } from "react-native"
import { useUnistyles } from "react-native-unistyles"

import { useReduceMotion } from "./useReduceMotion"

export type BreathPhase = "in" | "out"

export type Breathing = {
    breath: BreathPhase
    /** 0 expiré, 1 inspiré. */
    progress: Animated.Value
    /** Mouvement réduit : la bulle ne bouge pas, le mot ne change pas. */
    still: boolean
}

export function useBreathing(half: number): Breathing {
    const reduceMotion = useReduceMotion()
    const { motion } = useUnistyles().theme
    const [x1, y1, x2, y2] = motion.easing
    const [breath, setBreath] = useState<BreathPhase>("in")
    const progress = useRef(new Animated.Value(1)).current

    useEffect(() => {
        if (reduceMotion) {
            progress.setValue(1)
            setBreath("in")
            return
        }
        // react-native-web n'a pas de pilote natif ; y prétendre ne fait qu'avertir.
        const useNativeDriver = Platform.OS !== "web"
        const easing = Easing.bezier(x1, y1, x2, y2)
        let phase: BreathPhase = "in"
        let timer: ReturnType<typeof setTimeout> | undefined
        // Le minuteur mène le rythme, la bulle le suit : le mot et la taille ne se décalent jamais.
        const take = () => {
            setBreath(phase)
            Animated.timing(progress, {
                toValue: phase === "in" ? 1 : 0,
                duration: half,
                easing,
                useNativeDriver,
            }).start()
            phase = phase === "in" ? "out" : "in"
            timer = setTimeout(take, half)
        }
        progress.setValue(0)
        take()
        return () => {
            if (timer != null) clearTimeout(timer)
            progress.stopAnimation()
        }
    }, [reduceMotion, half, progress, x1, y1, x2, y2])

    return { breath, progress, still: reduceMotion }
}
